/**
 * Geofence geometry (S027; REQ-DAT-013, REQ-DISP-009).
 *
 * Pure geometry: circle, polygon, multipolygon, and corridor (a centreline with a width), plus a
 * point-in-geometry test and a validator. Distances are in metres; a small local equirectangular projection
 * around the query point keeps segment distances metric at city scale. Boundary points count as inside. The
 * durable store is PostGIS (REQ-DAT-013); this is the evaluation kernel the service and tests share.
 */
import { haversineMeters, isLatLng } from '@vello/geo';
const METERS_PER_DEG_LAT = 111_320;
/** Offset of `to` from `from` in metres, using a local equirectangular projection centred on `from`. */
function metersDelta(from, to) {
    const mPerLng = METERS_PER_DEG_LAT * Math.cos((from.latitude * Math.PI) / 180);
    return { x: (to.longitude - from.longitude) * mPerLng, y: (to.latitude - from.latitude) * METERS_PER_DEG_LAT };
}
/** Distance in metres from point `p` to segment `a`–`b`. */
function distanceToSegmentMeters(p, a, b) {
    const A = metersDelta(p, a);
    const B = metersDelta(p, b);
    const dx = B.x - A.x;
    const dy = B.y - A.y;
    const len2 = dx * dx + dy * dy;
    if (len2 === 0)
        return Math.hypot(A.x, A.y);
    let t = -(A.x * dx + A.y * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(A.x + t * dx, A.y + t * dy);
}
function pointInRing(ring, p) {
    // On-boundary (within 0.5 m of any edge) counts as inside.
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        if (distanceToSegmentMeters(p, ring[j], ring[i]) <= 0.5)
            return true;
    }
    // Ray casting on the lat/lng plane.
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const yi = ring[i].latitude;
        const xi = ring[i].longitude;
        const yj = ring[j].latitude;
        const xj = ring[j].longitude;
        const intersects = yi > p.latitude !== yj > p.latitude &&
            p.longitude < ((xj - xi) * (p.latitude - yi)) / (yj - yi) + xi;
        if (intersects)
            inside = !inside;
    }
    return inside;
}
/** Whether a point lies inside a geometry (boundary inclusive). Assumes valid geometry (see {@link validateGeometry}). */
export function contains(geometry, p) {
    switch (geometry.kind) {
        case 'circle':
            return haversineMeters(geometry.center, p) <= geometry.radiusMeters;
        case 'polygon':
            return pointInRing(geometry.ring, p);
        case 'multipolygon':
            return geometry.polygons.some((ring) => pointInRing(ring, p));
        case 'corridor': {
            const half = geometry.widthMeters / 2;
            for (let i = 1; i < geometry.path.length; i++) {
                if (distanceToSegmentMeters(p, geometry.path[i - 1], geometry.path[i]) <= half)
                    return true;
            }
            return false;
        }
    }
}
/** Validate a geometry, returning a stable reason or `null` when valid. */
export function validateGeometry(geometry) {
    switch (geometry.kind) {
        case 'circle':
            if (!isLatLng(geometry.center))
                return 'INVALID_COORDINATE';
            if (!(Number.isFinite(geometry.radiusMeters) && geometry.radiusMeters > 0))
                return 'NON_POSITIVE_RADIUS';
            return null;
        case 'polygon':
            if (geometry.ring.length < 3)
                return 'DEGENERATE_POLYGON';
            if (!geometry.ring.every(isLatLng))
                return 'INVALID_COORDINATE';
            return null;
        case 'multipolygon':
            if (geometry.polygons.length === 0)
                return 'EMPTY_MULTIPOLYGON';
            for (const ring of geometry.polygons) {
                if (ring.length < 3)
                    return 'DEGENERATE_POLYGON';
                if (!ring.every(isLatLng))
                    return 'INVALID_COORDINATE';
            }
            return null;
        case 'corridor':
            if (geometry.path.length < 2)
                return 'SHORT_CORRIDOR';
            if (!geometry.path.every(isLatLng))
                return 'INVALID_COORDINATE';
            if (!(Number.isFinite(geometry.widthMeters) && geometry.widthMeters > 0))
                return 'NON_POSITIVE_WIDTH';
            return null;
    }
}
//# sourceMappingURL=geometry.js.map